import React, { useMemo } from "react";

import { Theme, useTheme } from "@mui/material/styles";

import {
  FullWidthFieldContainer,
  FullWidthTextField,
  RowContainer,
  ShortFieldContainer,
  SingleItemRowContainer,
  getMuiLocalizationProviderAdapterName,
  logDebug,
  mergeProperties,
  packableDeepClone,
} from "packable-components";
import {
  OrderActionsProps,
  OrderItemAction,
  ShipmentType,
  ShippingStatus,
} from "./Order.types";
import { Grid, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/en"; // US
import "dayjs/locale/en-gb"; // UK
import "dayjs/locale/en-ca"; // CA

import OrderStatus from "./OrderStatus";

const getStyles = (theme: Theme) => ({
  OrderActionContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignContent: "center",
    alignItems: "center",
  },
  OrderActionContentShortTextField: {
    width: "240px",
  },
  PackableSalectableChip: {
    muiSize: "small",
  },
});

export const OrderPickupCompletedActions: React.FC<OrderActionsProps> = (
  props
): React.JSX.Element => {
  const { shipmentType, orderItemAction, onOrderItemActionUpdate, styles } =
    props;

  logDebug("OrderPickupCompletedActions PROPS");
  logDebug(props);

  const theme = useTheme();

  const defaultStyles = useMemo(() => getStyles(theme), [theme]);
  const effectiveStyles = useMemo(
    () =>
      styles === undefined
        ? defaultStyles
        : mergeProperties(defaultStyles, styles),
    [defaultStyles, styles]
  );

  logDebug("OrderPickupCompletedActions effectiveStyles");
  logDebug(effectiveStyles);

  const handleTrackingNumberValueChange = (event: any) => {
    let newOrderItemAction = packableDeepClone(orderItemAction);
    newOrderItemAction.trackingNumber = event.target.value;
    onOrderItemActionUpdate(newOrderItemAction);
  };

  const handleNoteValueChange = (event: any) => {
    let newOrderItemAction = packableDeepClone(orderItemAction);
    newOrderItemAction.note = event.target.value;
    onOrderItemActionUpdate(newOrderItemAction);
  };

  const onPickupDateChange = (event: any) => {
    if (event) {
      if (event instanceof dayjs) {
        const newValue = (event as Dayjs).toDate();
        let newOrderItemAction: OrderItemAction =
          packableDeepClone(orderItemAction);
        newOrderItemAction.actionDate = newValue;
        onOrderItemActionUpdate(newOrderItemAction);
      }
    }
  };

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    selection: ShippingStatus
  ) => {
    let newOrderItemAction = packableDeepClone(orderItemAction);
    newOrderItemAction.shippingStatus = selection;
    onOrderItemActionUpdate(newOrderItemAction);
  };

  const minDate = dayjs().subtract(7, "days");
  const maxDate = dayjs();
  const startTime = dayjs().set("hour", 0).set("minute", 0); // 12:00 AM
  const endTime = dayjs().set("hour", 23).set("minute", 59); // 12:59 PM

  const children = [
    <ToggleButton
      value={ShippingStatus.PICKUP_COMPLETED}
      key={ShippingStatus.PICKUP_COMPLETED}
    >
      <OrderStatus
        status={ShippingStatus.PICKUP_COMPLETED}
        isAdmin={true}
        displayChipForZeroCount
        disabled={
          orderItemAction.shippingStatus !== ShippingStatus.PICKUP_COMPLETED
        }
        styles={effectiveStyles}
      />
    </ToggleButton>,
    <ToggleButton
      value={ShippingStatus.DELIVERED}
      key={ShippingStatus.DELIVERED}
    >
      <OrderStatus
        status={ShippingStatus.DELIVERED}
        isAdmin={true}
        label="Delivered"
        disabled={orderItemAction.shippingStatus !== ShippingStatus.DELIVERED}
        displayChipForZeroCount
        styles={effectiveStyles}
      />
    </ToggleButton>,
  ];

  const control = {
    value: orderItemAction.shippingStatus,
    onChange: handleChange,
    exclusive: true,
  };

  return (
    <>
      <Grid sx={effectiveStyles.OrderActionContainer}>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <ToggleButtonGroup size="small" {...control} aria-label="Small sizes">
            {children}
          </ToggleButtonGroup>
        </Stack>
        {orderItemAction.shippingStatus === ShippingStatus.DELIVERED &&
          shipmentType === ShipmentType.STORAGE && (
            <SingleItemRowContainer>
              <FullWidthFieldContainer>
                <FullWidthTextField
                  id="trackingNumber"
                  label="Storage Tracking Number"
                  value={orderItemAction.trackingNumber}
                  onChange={handleTrackingNumberValueChange}
                />
              </FullWidthFieldContainer>
            </SingleItemRowContainer>
          )}
        {orderItemAction.shippingStatus === ShippingStatus.DELIVERED && (
          <RowContainer styles={{ PackableRowContainer: { pt: 1 } }}>
            <ShortFieldContainer>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={getMuiLocalizationProviderAdapterName()}
              >
                <DatePicker
                  className="OrderActionContentShortTextField"
                  sx={effectiveStyles.OrderActionContentShortTextField}
                  label="Delivered Date"
                  value={dayjs(orderItemAction.actionDate || new Date())}
                  onChange={onPickupDateChange}
                  minDate={minDate}
                  maxDate={maxDate}
                />
              </LocalizationProvider>
            </ShortFieldContainer>
            <ShortFieldContainer>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={getMuiLocalizationProviderAdapterName()}
              >
                <TimePicker
                  className="OrderActionContentShortTextField"
                  sx={effectiveStyles.OrderActionContentShortTextField}
                  label="Delivered Time"
                  value={dayjs(orderItemAction.actionDate || new Date())}
                  onChange={onPickupDateChange}
                  minTime={startTime}
                  maxTime={endTime}
                />
              </LocalizationProvider>
            </ShortFieldContainer>
          </RowContainer>
        )}
        {orderItemAction.shippingStatus === ShippingStatus.DELIVERED && (
          <>
            <SingleItemRowContainer>
              <FullWidthFieldContainer>
                <FullWidthTextField
                  id="note"
                  label="Note"
                  value={orderItemAction.note}
                  onChange={handleNoteValueChange}
                />
              </FullWidthFieldContainer>
            </SingleItemRowContainer>
          </>
        )}
      </Grid>
    </>
  );
};

export default OrderPickupCompletedActions;
